import React from "react";
import { Box, Typography } from "@mui/material";
import BarChart from "../../BarChart";
import PieChart from "../../PieChart";
import AreaChart from "../../AreaChart";
import HorizontalBarList from "../../HorizontalBarList";
import StatusList from "../../StatusList";
import HorizontalBarChart from "../../HorizontalBarChart";
import {
  ContractOverviewResponse,
  ContractAnalyticsResponse,
} from "../../../../types/contracts";
import metric1 from "../../../../assets/images/metric1.svg";
import metric2 from "../../../../assets/images/metric2.svg";
import metric3 from "../../../../assets/images/metric3.svg";
import metric4 from "../../../../assets/images/metric4.svg";
import expiringWeeks from "../../../../assets/images/calenderIcon.svg";
import expiringDays from "../../../../assets/images/stat.icon.svg";
import avgCct from "../../../../assets/images/overview.svg";
import closedRate from "../../../../assets/images/successImg.svg";
import styles from "./overviewDashboard.module.scss";

interface OverviewDashboardProps {
  overviewData: ContractOverviewResponse;
  analyticsData: ContractAnalyticsResponse;
}

interface CardConfig {
  key: string;
  icon: string;
}

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  overviewData,
  analyticsData,
}) => {
  const primaryCardConfigs: CardConfig[] = [
    { key: "totalContracts", icon: metric4 },
    { key: "closedContracts", icon: metric1 },
    { key: "inProcessContracts", icon: metric2 },
    { key: "expiredContracts", icon: metric3 },
  ];

  const secondaryCardConfigs = [
    {
      key: "expiringInTwoWeeks",
      label: "Expiring in 2 weeks",
      icon: expiringWeeks,
      iconColor: "#F9FAFB",
    },
    {
      key: "expiringInSixtyDays",
      label: "Expiring in 60 days",
      icon: expiringDays,
      iconColor: "#F9FAFB",
    },
    {
      key: "avgCctDays",
      label: "Avg CCT (Days)",
      icon: avgCct,
      iconColor: "#F9FAFB",
    },
    {
      key: "closedRatePct",
      label: "Closed Rate",
      icon: closedRate,
      iconColor: "#F9FAFB",
      suffix: "%",
    },
  ];

  const renderPrimaryCards = () => {
    if (!overviewData.primaryCards) return null;

    return primaryCardConfigs.map((config, index) => {
      const card =
        overviewData.primaryCards[
          config.key as keyof typeof overviewData.primaryCards
        ];
      if (!card) return null;

      return (
        <Box key={index} className={styles.metricCard}>
          <Box className={styles.metricIconWrapper}>
            <Box className={styles.metricIcon}>
              <img
                src={config.icon}
                alt={config.key}
                className={styles.metricImage}
              />
            </Box>
          </Box>
          <Box className={styles.metricContent}>
            <Typography variant="h3" className={styles.metricValue}>
              {card.count}
            </Typography>
            <Typography variant="body2" className={styles.metricLabel}>
              {card.label}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  const renderSecondaryCards = () => {
    if (!overviewData.secondaryCards) return null;

    return secondaryCardConfigs.map((config, index) => {
      const value =
        overviewData.secondaryCards[
          config.key as keyof typeof overviewData.secondaryCards
        ];
      if (value === undefined || value === null) return null;

      return (
        <Box key={index} className={styles.secondaryMetricCard}>
          <Box
            className={styles.secondaryMetricIcon}
            style={{ backgroundColor: config.iconColor }}
          >
            <img
              src={config.icon}
              alt={config.label}
              className={styles.metricImage}
            />
          </Box>
          <Box className={styles.secondaryMetricContent}>
            <Typography variant="body2" className={styles.secondaryMetricLabel}>
              {config.label}
            </Typography>
            <Typography variant="h5" className={styles.secondaryMetricValue}>
              {value}
              {config.suffix || ""}
            </Typography>
          </Box>
        </Box>
      );
    });
  };

  return (
    <>
      <Box className={styles.primaryMetrics}>{renderPrimaryCards()}</Box>

      <Box className={styles.secondaryMetrics}>{renderSecondaryCards()}</Box>

      <Box className={styles.chartsSection}>
        <BarChart
          title="Contracts By Year"
          data={overviewData.contractsByYear || []}
          xAxisKey="year"
        />
        <PieChart
          title="Contracts By Group"
          data={analyticsData.contractsByGroupDetailed || []}
          nameKey="group"
          valueKey="count"
        />
      </Box>

      <Box className={styles.fullWidthChart}>
        <AreaChart
          title="All Contracts (Total vs Open)"
          data={analyticsData.allContractsTotalVsOpen || []}
          xAxisKey="year"
        />
      </Box>

      <Box className={styles.chartsGrid}>
        <HorizontalBarList
          title="Contracts By Type"
          data={analyticsData.contractsByType || []}
          labelKey="contractType"
          valueKey="count"
        />
        <StatusList
          title="Contracts By Status"
          data={analyticsData.contractsByStatus || []}
          labelKey="status"
          valueKey="count"
        />
      </Box>

      <Box className={styles.fullWidthChart}>
        <HorizontalBarChart
          title="Contracts By Group (Detailed)"
          data={analyticsData.contractsByGroupDetailed || []}
          nameKey="group"
          valueKey="count"
          valueLabel="contracts"
        />
      </Box>
    </>
  );
};

export default OverviewDashboard;
